import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { Reveal, LineReveal, Eyebrow, SecondaryLink, PrimaryButton } from "../components/primitives";
import StatCounter from "../components/StatCounter";
import CTABand from "../components/CTABand";
import { STATS, FEATURED_WORK, SERVICES, CLIENTS, TESTIMONIALS, IMAGES } from "../data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden bg-night" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={IMAGES.heroHome} alt="Editorial fashion" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/70" />
      </motion.div>

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col justify-end pb-24 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <Eyebrow className="text-gold">Fashion-Native Performance Marketing</Eyebrow>
        </motion.div>

        <h1 className="mt-6 font-serif font-light text-ivory leading-[0.98] tracking-[-0.01em] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5vw] max-w-6xl">
          <LineReveal
            delay={0.3}
            lines={["Performance marketing", "that scales fashion", "& beauty brands."]}
          />
        </h1>

        <motion.p
          className="mt-8 max-w-xl font-sans text-base md:text-lg text-ivory/80 leading-[1.7]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          We take D2C fashion, beauty and luxury brands to 5x+ ROAS through ads,
          influencers, and creative storytelling.
        </motion.p>

        <motion.div
          className="mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
        >
          <PrimaryButton light testId="hero-book-call" />
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-ivory/60">Scroll</span>
        <motion.div
          className="w-px h-10 bg-ivory/40"
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="bg-ivory border-b border-taupe" data-testid="stats-bar">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className="text-center md:text-left">
            <div className="font-serif font-light text-6xl md:text-7xl lg:text-8xl text-charcoal leading-none tracking-tight">
              <StatCounter value={s.value} suffix={s.suffix} decimals={s.decimals} display={s.display} />
            </div>
            <p className="mt-4 font-sans text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {s.label}
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Intro() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <section className="bg-ivory" data-testid="intro-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div ref={ref} className="relative overflow-hidden rounded-sm bg-taupe aspect-[4/5]">
                <motion.img
                  src={IMAGES.founder}
                  alt="Vaishnavi Modi, Founder of Connect.Co"
                  className="w-full h-full object-cover"
                  style={{ y, scale: 1.12 }}
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal><Eyebrow>The Agency</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif font-light text-charcoal leading-[1.1] tracking-tight text-3xl md:text-5xl lg:text-[3.4rem]">
                We&rsquo;re a fashion-native performance agency. We don&rsquo;t run the
                generic playbook. We build fashion brands the way fashion actually works.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10">
                <SecondaryLink to="/about" testId="meet-founder-link">Meet the Founder</SecondaryLink>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedWork() {
  return (
    <section className="bg-ivory" data-testid="featured-work-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pb-28 md:pb-40">
        <div className="flex items-end justify-between mb-14">
          <Reveal><Eyebrow>Selected Work</Eyebrow></Reveal>
          <Reveal delay={0.1}><SecondaryLink to="/work" testId="see-all-work-link">See all work</SecondaryLink></Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {FEATURED_WORK.map((w, i) => (
            <Reveal key={w.key} delay={i * 0.1}>
              <Link to="/work" data-testid={`work-card-${w.key}`} className="group block">
                <div className="relative overflow-hidden rounded-sm bg-taupe aspect-[3/4]">
                  <img
                    src={w.image}
                    alt={w.brand}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60" />
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <h3 className="font-serif text-2xl md:text-3xl text-charcoal font-normal">{w.brand}</h3>
                  <span className="font-serif text-2xl text-gold">{w.stat}</span>
                </div>
                <p className="font-sans text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
                  {w.statLabel}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesTeaser() {
  return (
    <section className="bg-night text-ivory" data-testid="services-teaser">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow>What We Do</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif font-light text-ivory text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Built to scale, led by performance.
              </h2>
              <div className="mt-10">
                <SecondaryLink to="/services" testId="explore-services-link" light>Explore our services</SecondaryLink>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            <ul className="divide-y divide-ivory/12">
              {SERVICES.map((s, i) => (
                <Reveal key={s.name} delay={i * 0.05}>
                  <li className="group flex items-center justify-between py-5 md:py-6">
                    <span className="font-serif text-2xl md:text-4xl text-ivory/90 group-hover:text-gold transition-colors duration-300 font-light">
                      {s.name}
                    </span>
                    <span className="font-sans text-xs text-ivory/30">{String(i + 1).padStart(2, "0")}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClientStrip() {
  return (
    <section className="bg-ivory" data-testid="client-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-28 md:pt-40">
        <div className="flex items-end justify-between mb-12">
          <Reveal><Eyebrow>Trusted By</Eyebrow></Reveal>
          <Reveal delay={0.1}><SecondaryLink to="/clients" testId="our-clients-link">Our clients</SecondaryLink></Reveal>
        </div>
      </div>
      <div className="border-y border-taupe py-10">
        <Marquee speed={40} gradient gradientColor="#FAF8F3" gradientWidth={120} pauseOnHover>
          {CLIENTS.map((c) => (
            <span
              key={c}
              className="font-serif text-2xl md:text-3xl text-charcoal/40 hover:text-gold transition-colors duration-300 mx-10 whitespace-nowrap"
            >
              {c}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function TestimonialPull() {
  const featured = [6, 2, 1].map((i) => TESTIMONIALS[i]);
  return (
    <section className="bg-taupe" data-testid="home-testimonial">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <div className="flex items-end justify-between mb-14">
          <Reveal><Eyebrow>In Their Words</Eyebrow></Reveal>
          <Reveal delay={0.1}><SecondaryLink to="/clients" testId="read-all-testimonials">Read all</SecondaryLink></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {featured.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <figure className="h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-ivory ring-1 ring-gold/30 shrink-0">
                    {t.photo && <img src={t.photo} alt={t.brand} className="w-full h-full object-cover object-top" />}
                  </div>
                  <figcaption className="font-sans text-[11px] uppercase tracking-[0.18em] text-gold leading-tight">
                    {t.name ? `${t.name}` : t.brand}
                    {t.name && <span className="block text-charcoal/50 mt-1">{t.brand}</span>}
                  </figcaption>
                </div>
                <blockquote className="font-serif font-light text-charcoal text-lg md:text-xl leading-[1.5] italic line-clamp-[8]">
                  “{t.quote}”
                </blockquote>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FromZeroTeaser() {
  return (
    <section className="bg-ivory border-b border-taupe" data-testid="from-zero-teaser">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-2">
            <Reveal><Eyebrow>From Zero</Eyebrow></Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.05}>
              <h2 className="font-serif font-light text-charcoal text-3xl md:text-5xl leading-[1.1] tracking-tight">
                We do not just scale brands. We start them.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 font-sans text-sm md:text-base text-charcoal/60 leading-[1.7]">
                En:EM, On The Racks, Shortbread and more, all built from zero sales.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-3 md:text-right">
            <Reveal delay={0.15}>
              <Link
                to="/work#from-scratch"
                data-testid="from-zero-link"
                className="group relative inline-flex items-center gap-2 font-sans text-sm tracking-[0.15em] uppercase text-charcoal"
              >
                <span className="relative">
                  See how we build from scratch
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold transition-all duration-300 ease-out group-hover:w-full" />
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <StatsBar />
      <FromZeroTeaser />
      <Intro />
      <FeaturedWork />
      <ServicesTeaser />
      <ClientStrip />
      <TestimonialPull />
      <CTABand title="Book a strategy call and let's scale your brand." />
    </>
  );
}
